// Pirate Ship — a toy pirate galleon standing upright on a flat keel, like the
// prized piece of a playroom fleet: a fat brown hull with a cream stripe, a
// raised stern castle, one mast with a square sail and a crow's nest, and a
// cute dark pennant snapping at the masthead. Toy-like: plump silhouette,
// oversized portholes, warm flat colours.
//
// Frame: y = 0 is the floor (the flat keel stands on it), +Z is the bow.
// About 3 m long over a 2x3 tile pad.
//
// Animation layout (flat nodes, deliberate pivots):
//   - "ship" : everything except the pennant. Rolls and pitches gently in idle
//              as if riding a remembered swell. Pivot low at the keel.
//   - "flag" : the pennant at the masthead. The idle animation drives it with
//              the exact same track as "ship" (same pivot, same keys) so the
//              two move as one rigid body; the flag-wave animation then swings
//              it alone about the masthead.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const BROWN = "#8a5a33";      // main hull — warm toy brown
const BROWN_DARK = "#6b4423"; // shaded hull (keel, lower strake, mast base)
const DECK = "#c19a6b";       // deck planking — lighter warm wood
const CREAM = "#f6efe3";      // trim: hull stripe, sail, stern windows
const RED = "#d9534a";        // accents: rail caps, sail stripe, castle roof
const FLAG = "#3b4a6b";       // the pennant — cute dark navy (never pure black)
const GLASS = "#8fd3df";      // porthole glass

// Frame measures.
const MAST_Z = 0.1;                        // the mast's position along the hull
const MASTHEAD_Y = 2.62;                   // where the pennant attaches
const SHIP_PIVOT: [number, number, number] = [0, 0.2, 0];
const FLAG_PIVOT: [number, number, number] = [0, MASTHEAD_Y, MAST_Z];

// The idle swell, shared verbatim by the "ship" and "flag" tracks so the
// pennant rides the hull rigidly while the hull rocks.
const SWELL: { t: number; value: [number, number, number]; ease: "sine" }[] = [
  { t: 0, value: [0.012, 0, 0.022], ease: "sine" },
  { t: 2.1, value: [-0.012, 0, -0.022], ease: "sine" },
  { t: 4.2, value: [0.012, 0, 0.022], ease: "sine" },
];

// A porthole on one flank: cream ring plus glass disc, facing +-X.
function porthole(x: number, z: number): Part[] {
  const s = Math.sign(x);
  return [
    { geometry: new THREE.CylinderGeometry(0.075, 0.075, 0.05, 10), color: CREAM,
      position: [x, 0.52, z], rotation: [0, 0, Math.PI / 2], name: "ship" },
    { geometry: new THREE.CylinderGeometry(0.048, 0.048, 0.03, 10), color: GLASS,
      position: [x + s * 0.02, 0.52, z], rotation: [0, 0, Math.PI / 2], name: "ship" },
  ];
}

function shipParts(): Part[] {
  return [
    // Flat keel: the display base the galleon stands on.
    { geometry: new THREE.BoxGeometry(0.26, 0.16, 2.3), color: BROWN_DARK,
      position: [0, 0.08, 0.05], name: "ship" },
    // Lower strake: the hull's dark waterline layer, tucked in from the beam.
    { geometry: new THREE.BoxGeometry(0.72, 0.28, 2.35), color: BROWN_DARK,
      position: [0, 0.29, 0.05], name: "ship" },
    // Main hull: the fat brown belly.
    { geometry: new THREE.BoxGeometry(0.95, 0.36, 2.6), color: BROWN,
      position: [0, 0.60, 0.05], name: "ship" },
    // Cream stripe wrapping the hull just under the rail.
    { geometry: new THREE.BoxGeometry(0.99, 0.10, 2.64), color: CREAM,
      position: [0, 0.70, 0.05], name: "ship" },

    // Deck planking laid over the hull top.
    { geometry: new THREE.BoxGeometry(0.85, 0.05, 2.45), color: DECK,
      position: [0, 0.80, 0.05], name: "ship" },
    // Bulwark rails down each side, capped in red.
    { geometry: new THREE.BoxGeometry(0.07, 0.16, 2.4), color: BROWN,
      position: [-0.44, 0.88, 0.05], name: "ship" },
    { geometry: new THREE.BoxGeometry(0.07, 0.16, 2.4), color: BROWN,
      position: [0.44, 0.88, 0.05], name: "ship" },
    { geometry: new THREE.BoxGeometry(0.09, 0.045, 2.42), color: RED,
      position: [-0.44, 0.98, 0.05], name: "ship" },
    { geometry: new THREE.BoxGeometry(0.09, 0.045, 2.42), color: RED,
      position: [0.44, 0.98, 0.05], name: "ship" },

    // The prow: a plump cone nosing out of the bow, plus a bowsprit spearing
    // up and forward. The cone points +Z (its +Y axis rotated onto Z).
    { geometry: new THREE.ConeGeometry(0.42, 0.7, 10), color: BROWN,
      position: [0, 0.56, 1.65], rotation: [Math.PI / 2, 0, 0], scale: [1, 1, 0.75], name: "ship" },
    { geometry: new THREE.CylinderGeometry(0.035, 0.05, 0.75, 8), color: BROWN_DARK,
      position: [0, 0.98, 1.75], rotation: [Math.PI / 2 - 0.5, 0, 0], name: "ship" },

    // Stern castle: the raised cabin at the back with a red roof and two cream
    // windows on the transom.
    { geometry: new THREE.BoxGeometry(0.88, 0.38, 0.72), color: BROWN,
      position: [0, 1.0, -0.95], name: "ship" },
    { geometry: new THREE.BoxGeometry(0.96, 0.09, 0.8), color: RED,
      position: [0, 1.23, -0.95], name: "ship" },
    { geometry: new THREE.CylinderGeometry(0.08, 0.08, 0.05, 10), color: CREAM,
      position: [-0.2, 1.0, -1.31], rotation: [Math.PI / 2, 0, 0], name: "ship" },
    { geometry: new THREE.CylinderGeometry(0.08, 0.08, 0.05, 10), color: CREAM,
      position: [0.2, 1.0, -1.31], rotation: [Math.PI / 2, 0, 0], name: "ship" },

    // Portholes: two per flank along the cream stripe's level.
    ...porthole(-0.485, 0.55), ...porthole(-0.485, -0.15),
    ...porthole(0.485, 0.55), ...porthole(0.485, -0.15),

    // The mast: a stout pole from deck to masthead, with a dark boot at deck
    // level and a round masthead cap.
    { geometry: new THREE.CylinderGeometry(0.10, 0.12, 0.14, 10), color: BROWN_DARK,
      position: [0, 0.87, MAST_Z], name: "ship" },
    { geometry: new THREE.CylinderGeometry(0.05, 0.065, 1.9, 10), color: BROWN_DARK,
      position: [0, 1.75, MAST_Z], name: "ship" },
    { geometry: new THREE.SphereGeometry(0.07, 8, 6), color: RED,
      position: [0, 2.72, MAST_Z], name: "ship" },

    // The yard (crossbeam) and the square sail hanging just forward of it,
    // with a red stripe across the middle.
    { geometry: new THREE.CylinderGeometry(0.035, 0.035, 1.25, 8), color: BROWN_DARK,
      position: [0, 2.2, MAST_Z], rotation: [0, 0, Math.PI / 2], name: "ship" },
    { geometry: new THREE.BoxGeometry(1.05, 0.85, 0.05), color: CREAM,
      position: [0, 1.76, MAST_Z + 0.08], name: "ship" },
    { geometry: new THREE.BoxGeometry(1.07, 0.16, 0.06), color: RED,
      position: [0, 1.76, MAST_Z + 0.08], name: "ship" },

    // Crow's nest: a little cup below the masthead.
    { geometry: new THREE.CylinderGeometry(0.13, 0.10, 0.14, 10), color: BROWN,
      position: [0, 2.42, MAST_Z], name: "ship" },

    // The pennant: a tapered dark flag trailing aft from the masthead, in two
    // pieces so the tail thins out. Named "flag" so it waves on its own.
    { geometry: new THREE.BoxGeometry(0.035, 0.19, 0.28), color: FLAG,
      position: [0, MASTHEAD_Y, MAST_Z - 0.17], name: "flag" },
    { geometry: new THREE.BoxGeometry(0.035, 0.11, 0.2), color: FLAG,
      position: [0, MASTHEAD_Y, MAST_Z - 0.4], name: "flag" },
  ];
}

export const pirateShip = defineModel({
  id: "pirate-ship",
  batchId: "mess",
  name: "Pirate Ship",
  tags: ["mess", "cute", "animated", "vehicle", "ship", "pirate", "boat", "sea", "toy"],
  summary: "A toy pirate galleon on a flat keel, square sail set and pennant waving.",
  description:
    "A playroom pirate galleon standing upright on its flat keel: a fat brown " +
    "hull wrapped in a cream stripe, red-capped rails, a raised stern castle " +
    "with a red roof, a plump prow and bowsprit, and one stout mast carrying a " +
    "square cream sail, a crow's nest and a dark navy pennant. The whole hull " +
    "rolls and pitches gently in idle as if riding a remembered swell, and the " +
    "pennant snaps back and forth in its signature wave.",
  author: "models",
  footprint: 1.5,
  tiles: { w: 2, h: 3 },
  parts: shipParts,
  animations: [
    // Idle — the remembered swell: the whole ship rolls and pitches about the
    // keel. The flag node is driven with the identical track so it rides the
    // masthead rigidly instead of being left behind.
    {
      id: "idle",
      label: "Gentle swell",
      duration: 4.2,
      loop: true,
      tracks: [
        { target: "ship", pivot: SHIP_PIVOT, rotation: SWELL },
        { target: "flag", pivot: SHIP_PIVOT, rotation: SWELL },
      ],
    },

    // Flag wave — the signature move: the pennant snaps side to side about the
    // masthead, with a little upward flick at each swing.
    {
      id: "flag-wave",
      label: "Pennant wave",
      duration: 1.8,
      loop: true,
      tracks: [
        {
          target: "flag", pivot: FLAG_PIVOT,
          rotation: [
            { t: 0, value: [0, 0.3, 0.03], ease: "easeInOut" },
            { t: 0.45, value: [0, -0.35, -0.05], ease: "easeInOut" },
            { t: 0.9, value: [0, 0.4, 0.04], ease: "easeInOut" },
            { t: 1.35, value: [0, -0.25, -0.03], ease: "easeInOut" },
            { t: 1.8, value: [0, 0.3, 0.03], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
